// usePaymentForm.ts
import { useState, useCallback } from 'react'
import { getApi } from '../../../api/api'
import { TariffRule } from '../../../common/types/interfaces'
import { useTariff } from '../../../common/hooks/useTariff'
import { format } from 'date-fns'
import { ru } from 'date-fns/locale'
import { delayAction } from '../../../common/utils/functions/delayAction'

type UsePaymentFormProps = {
  clusterId: number
  tariff: TariffRule | undefined
  monthlyPrice: number
  agreement?: string
}

const getPeriodText = (months: number): string => {
  const now = new Date()

  if (months === 1) {
    return `за ${format(now, 'LLLL yyyy', { locale: ru })}`
  }

  const monthNames: string[] = []
  for (let i = 0; i < months; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() + i, 1)
    monthNames.push(format(date, 'LLLL', { locale: ru }))
  }

  return `за ${months} мес.: ${monthNames.join(', ')} ${format(now, 'yyyy')}`
}

export const usePaymentForm = ({
  clusterId,
  tariff,
  monthlyPrice,
  agreement,
}: UsePaymentFormProps) => {
  const [paymentLoading, setPaymentLoading] = useState(false)
  const [invoiceLoading, setInvoiceLoading] = useState(false)
  const [yoomoneyToken, setYoomoneyToken] = useState<string | null>(null)
  const [paymentId, setPaymentId] = useState<string | null>(null)
  const [selectedDuration, setSelectedDuration] = useState<number>(1)
  const api = getApi()
  const { calculatePrice, getAvailableDurations } = useTariff()

  const availableDurations = getAvailableDurations(tariff)
  const price = calculatePrice(tariff, selectedDuration)

  const startPayment = useCallback(async () => {
    setPaymentLoading(true)
    try {
      const result = await api.startPayment({
        clusterId,
        durationMonths: selectedDuration,
        price,
        description: `Оплата за аренду платформы по договору ${agreement ? agreement + ' ' : ''}${getPeriodText(selectedDuration)}`,
        quantity: 1,
      })
      setYoomoneyToken(result.token)
      setPaymentId(result.id)
    } catch (error) {
      console.error('Payment error:', error)
    } finally {
      setPaymentLoading(false)
    }
  }, [clusterId, selectedDuration, price, agreement, api])

  const requestInvoice = useCallback(async () => {
    setInvoiceLoading(true)
    try {
      await api.sendInvoice({ clusterId, durationMonths: selectedDuration })
      await delayAction(500)
    } catch (error) {
      throw error
    } finally {
      await delayAction(500)
      setInvoiceLoading(false)
    }
  }, [clusterId, selectedDuration, api])

  return {
    paymentLoading,
    invoiceLoading,
    yoomoneyToken,
    paymentId,
    startPayment,
    requestInvoice,
    selectedDuration,
    setSelectedDuration,
    availableDurations,
    price,
    monthlyPrice,
  }
}
